import React, { useEffect, useMemo, useState } from 'react';
import { useI18n } from '../i18n/I18nProvider';
import {
  buildTrainingModeEditItems,
  extractHistoryItems,
  extractOverviewPendingItems,
  groupStudentVocabularyModes,
  normalizeStudentTrainingMode,
  STUDENT_TRAINING_MODE_COMPREHENSIVE,
  STUDENT_TRAINING_MODE_MEANING,
  STUDENT_TRAINING_MODES,
} from '../lib/studentVocabularyModeManager';

const modeLabelKeys = {
  [STUDENT_TRAINING_MODE_MEANING]: 'Meaning training',
  [STUDENT_TRAINING_MODE_COMPREHENSIVE]: 'Comprehensive training',
};

const bulkCommands = [
  { label: 'Meaning only', modes: [STUDENT_TRAINING_MODE_MEANING] },
  { label: 'Comprehensive only', modes: [STUDENT_TRAINING_MODE_COMPREHENSIVE] },
  { label: 'Both', modes: STUDENT_TRAINING_MODES },
];

const orderModes = (values) => {
  const set = new Set((values || []).map(normalizeStudentTrainingMode));
  return STUDENT_TRAINING_MODES.filter((mode) => set.has(mode));
};

const requestJson = async (method, url, body) => {
  const token = localStorage.getItem('token');
  const response = await fetch(url, {
    method,
    headers: {
      Authorization: `Bearer ${token}`,
      ...(body ? { 'Content-Type': 'application/json' } : {}),
    },
    body: body ? JSON.stringify(body) : undefined,
  });
  const payload = await response.json().catch(() => ({}));
  if (!response.ok) {
    const error = new Error(payload?.message || 'Request failed');
    error.status = response.status;
    error.payload = payload;
    throw error;
  }
  return payload;
};

const modeBadgeClass = (mode) => (
  mode === STUDENT_TRAINING_MODE_COMPREHENSIVE
    ? 'border-blue-300 bg-blue-50 text-blue-800'
    : 'border-emerald-300 bg-emerald-50 text-emerald-800'
);

const groupBorderClass = (modes) => {
  const values = orderModes(modes);
  if (values.length === 2) return 'border-emerald-200 bg-emerald-50/70';
  if (values.includes(STUDENT_TRAINING_MODE_COMPREHENSIVE)) return 'border-blue-300 bg-white';
  return 'border-emerald-300 bg-white';
};

const senseDisplayText = (group) => (
  String(group?.definition_cn || group?.definition_en || '').trim()
);

const senseActionLabel = (group, t, actionKey) => {
  const meaning = senseDisplayText(group);
  return meaning
    ? t(actionKey, { word: group.word, meaning })
    : group.word;
};

const ModeBadges = ({ modes, t }) => {
  const values = orderModes(modes);
  return (
    <span className="inline-flex min-w-0 flex-wrap gap-1" data-testid="mode-badges">
      {values.map((mode) => (
        <span
          key={mode}
          className={`inline-flex max-w-full items-center rounded border px-1.5 py-0.5 text-[11px] font-semibold leading-4 ${modeBadgeClass(mode)}`}
          data-mode={mode}
        >
          {t(modeLabelKeys[mode])}
        </span>
      ))}
    </span>
  );
};

const SenseSummary = ({ group }) => (
  <div className="min-w-0 flex-1">
    <div className="flex min-w-0 flex-wrap items-baseline gap-x-2 gap-y-0.5">
      <span className="break-words font-semibold text-gray-900" data-i18n-skip>{group.word}</span>
    </div>
    {(group.definition_cn || group.definition_en) && (
      <div className="mt-0.5 min-w-0 break-words text-sm text-gray-700" data-i18n-skip>
        {group.definition_cn || group.definition_en}
      </div>
    )}
    {group.definition_cn && group.definition_en && (
      <div className="mt-0.5 min-w-0 break-words text-xs text-gray-500" data-i18n-skip>
        {group.definition_en}
      </div>
    )}
  </div>
);

const copyGroup = (group) => ({
  ...group,
  pendingModes: [...(group.pendingModes || [])],
  completedModes: [...(group.completedModes || [])],
  modes: [...(group.modes || [])],
});

const StudentVocabularyModeManager = ({
  classId,
  studentId,
  overview,
  onSaved,
}) => {
  const { t, apiMessage } = useI18n();
  const [open, setOpen] = useState(false);
  const [historyItems, setHistoryItems] = useState(null);
  const [draftGroups, setDraftGroups] = useState([]);
  const [selectedKeys, setSelectedKeys] = useState(new Set());
  const [dirtyKeys, setDirtyKeys] = useState(new Set());
  const [historyLoading, setHistoryLoading] = useState(false);
  const [saveState, setSaveState] = useState('idle');
  const [managerError, setManagerError] = useState(null);
  const [confirmingRemoval, setConfirmingRemoval] = useState(false);

  const overviewGroups = useMemo(() => (
    groupStudentVocabularyModes(extractOverviewPendingItems(overview), { defaultPending: true })
  ), [overview]);

  const displayGroups = useMemo(() => (
    historyItems === null
      ? overviewGroups
      : groupStudentVocabularyModes(historyItems)
  ), [historyItems, overviewGroups]);

  const pendingGroups = useMemo(
    () => displayGroups.filter((group) => group.pending),
    [displayGroups],
  );

  const learnedGroups = useMemo(
    () => displayGroups.filter((group) => group.completed),
    [displayGroups],
  );

  const setRows = (rows, { defaultPending = false } = {}) => {
    const nextRows = defaultPending
      ? (Array.isArray(rows) ? rows : []).map((item) => (
        typeof item === 'string'
          ? { word: item, pending: true }
          : item && typeof item === 'object' && item.pending === undefined
            ? { ...item, pending: true }
            : item
      ))
      : rows;
    const groups = groupStudentVocabularyModes(nextRows, { defaultPending });
    setHistoryItems(nextRows);
    setDraftGroups(groups.map(copyGroup));
    setSelectedKeys(new Set());
    setDirtyKeys(new Set());
    setConfirmingRemoval(false);
  };

  const loadHistory = async () => {
    setHistoryLoading(true);
    setManagerError(null);
    try {
      if (!classId) {
        setRows(extractOverviewPendingItems(overview), { defaultPending: true });
        return;
      }
      const payload = await requestJson(
        'GET',
        `/api/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}/vocab-mission-history`,
      );
      setRows(extractHistoryItems(payload));
    } catch (error) {
      setManagerError(error);
    } finally {
      setHistoryLoading(false);
    }
  };

  useEffect(() => {
    setOpen(false);
    setHistoryItems(null);
    setDraftGroups([]);
    setSelectedKeys(new Set());
    setDirtyKeys(new Set());
    setManagerError(null);
    setSaveState('idle');
    setConfirmingRemoval(false);
  }, [classId, studentId]);

  const openManager = async (initialKey = null) => {
    setOpen(true);
    setSaveState('idle');
    if (historyItems === null) await loadHistory();
    if (initialKey) setSelectedKeys(new Set([initialKey]));
  };

  const closeManager = () => {
    setOpen(false);
    setManagerError(null);
    setSaveState('idle');
    setConfirmingRemoval(false);
  };

  const updateGroupModes = (key, requestedModes) => {
    setDraftGroups((current) => current.map((group) => {
      if (group.key !== key) return group;
      const pendingModes = orderModes(requestedModes || []);
      if (!pendingModes.length) return group;
      return {
        ...group,
        pendingModes,
        modes: orderModes([...(group.completedModes || []), ...pendingModes]),
      };
    }));
    setDirtyKeys((current) => new Set([...current, key]));
  };

  const toggleMode = (group, mode) => {
    if ((group.completedModes || []).includes(mode)) return;
    const hasMode = (group.pendingModes || []).includes(mode);
    if (hasMode && (group.pendingModes || []).length <= 1) return;
    const requestedModes = hasMode
      ? group.pendingModes.filter((value) => value !== mode)
      : [...group.pendingModes, mode];
    updateGroupModes(group.key, requestedModes);
  };

  const applyBulkCommand = (modes) => {
    if (!selectedKeys.size) return;
    setDraftGroups((current) => current.map((group) => {
      if (!selectedKeys.has(group.key) || !group.pending) return group;
      const pendingModes = orderModes(modes);
      return {
        ...group,
        pendingModes,
        modes: orderModes([...(group.completedModes || []), ...pendingModes]),
      };
    }));
    setDirtyKeys((current) => new Set([...current, ...selectedKeys]));
  };

  const selectAll = () => setSelectedKeys(new Set(pendingGroups.map((group) => group.key)));
  const clearSelection = () => setSelectedKeys(new Set());

  const removeSelected = async () => {
    const selectedGroups = draftGroups.filter((group) => (
      group.pending && selectedKeys.has(group.key)
    ));
    const items = buildTrainingModeEditItems(selectedGroups);
    if (!items.length) {
      setManagerError({ message: 'Choose at least one meaning.' });
      setSaveState('error');
      return;
    }
    setSaveState('saving');
    setManagerError(null);
    try {
      await requestJson(
        'DELETE',
        `/api/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}/assigned-word-items`,
        { action: 'delete', items },
      );
      setConfirmingRemoval(false);
      await loadHistory();
      if (onSaved) await onSaved();
      setSaveState('success');
    } catch (error) {
      setManagerError(error);
      setSaveState('error');
    }
  };

  const save = async () => {
    if (!classId) {
      setManagerError({ message: 'Could not save these meanings.' });
      setSaveState('error');
      return;
    }
    const changedGroups = draftGroups.filter((group) => dirtyKeys.has(group.key) && group.pending);
    const items = buildTrainingModeEditItems(changedGroups);
    if (!items.length || items.length !== changedGroups.length) {
      setManagerError({ message: 'Choose at least one meaning.' });
      setSaveState('error');
      return;
    }
    setSaveState('saving');
    setManagerError(null);
    try {
      await requestJson(
        'PATCH',
        `/api/classes/${encodeURIComponent(classId)}/students/${encodeURIComponent(studentId)}/assigned-word-items`,
        { action: 'training_modes', items },
      );
      await loadHistory();
      if (onSaved) await onSaved();
      setSaveState('success');
    } catch (error) {
      setManagerError(error);
      setSaveState('error');
    }
  };

  const errorText = managerError
    ? managerError.payload
      ? apiMessage(managerError.payload, 'This change could not be saved.')
      : t(managerError.message || 'This change could not be saved.')
    : '';

  const pendingDraftGroups = draftGroups.filter((group) => group.pending);
  const hasPending = pendingGroups.length > 0;

  return (
    <section className="bg-white p-6 rounded-xl shadow border border-gray-100" data-testid="student-vocabulary-mode-manager">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h4 className="font-bold text-gray-900">{t('Assigned meanings')}</h4>
          <p className="mt-1 text-sm text-gray-600">{t('{count} meanings', { count: pendingGroups.length })}</p>
        </div>
        {hasPending ? (
          <button
            type="button"
            onClick={() => openManager()}
            aria-expanded={open}
            className="w-full rounded border border-emerald-300 bg-emerald-50 px-3 py-2 text-left transition hover:border-emerald-500 hover:bg-emerald-100 focus:outline-none focus:ring-2 focus:ring-emerald-500 sm:w-auto"
            data-testid="pending-vocabulary-card"
          >
            <span className="block text-sm font-semibold text-emerald-900">{t('Pending')}</span>
            <span className="mt-1 block text-xs text-emerald-800">{t('{count} meanings', { count: pendingGroups.length })}</span>
          </button>
        ) : (
          <div className="rounded border border-gray-200 bg-gray-50 px-3 py-2 text-sm text-gray-500" data-testid="empty-pending-vocabulary">
            {t('No teacher-assigned meanings to learn')}
          </div>
        )}
      </div>

      {hasPending && (
        <div className="mt-3 flex min-w-0 flex-wrap items-center gap-2" data-testid="pending-vocabulary-chips">
          {pendingGroups.map((group) => (
            <button
              type="button"
              key={group.key}
              onClick={() => openManager(group.key)}
              className={`inline-flex min-w-0 max-w-full flex-wrap items-center gap-1 rounded border px-2 py-1 text-left text-xs transition hover:brightness-95 focus:outline-none focus:ring-2 focus:ring-emerald-500 ${groupBorderClass(group.pendingModes)}`}
              data-sense-id={group.sense_id}
              data-training-modes={orderModes(group.pendingModes).join(',')}
              aria-label={senseActionLabel(group, t, 'Open details for {word}: {meaning}')}
            >
              <span className="max-w-full break-words font-semibold text-gray-800" data-i18n-skip>{group.word}</span>
              {senseDisplayText(group) && (
                <span className="max-w-full break-words text-gray-600" data-i18n-skip>{senseDisplayText(group)}</span>
              )}
              <ModeBadges modes={group.pendingModes} t={t} />
            </button>
          ))}
        </div>
      )}

      {open && (
        <div className="mt-5 overflow-hidden rounded border border-gray-200" data-testid="vocabulary-mode-panel">
          <div className="flex flex-wrap items-start justify-between gap-3 border-b border-gray-200 bg-gray-50 px-4 py-3">
            <div className="min-w-0">
              <h5 className="font-semibold text-gray-900">{t('Training mode')}</h5>
              <p className="mt-1 text-sm text-gray-600">{t('{count} selected', { count: selectedKeys.size })}</p>
            </div>
            <button type="button" onClick={closeManager} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm text-gray-700 hover:bg-gray-100">
              {t('Close')}
            </button>
          </div>

          {historyLoading ? (
            <div className="p-4 text-sm text-gray-600">{t('Loading...')}</div>
          ) : (
            <>
              <div className="flex flex-wrap items-center gap-2 border-b border-gray-200 px-4 py-3">
                <button type="button" onClick={selectAll} disabled={!pendingDraftGroups.length} className="rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                  {t('Select all')}
                </button>
                <button type="button" onClick={clearSelection} disabled={!selectedKeys.size} className="rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50">
                  {t('Clear selection')}
                </button>
                <button
                  type="button"
                  onClick={() => setConfirmingRemoval(true)}
                  disabled={!selectedKeys.size || saveState === 'saving'}
                  className="rounded border border-red-300 bg-white px-2.5 py-1.5 text-sm font-semibold text-red-700 hover:bg-red-50 disabled:cursor-not-allowed disabled:opacity-50"
                >
                  {t('Remove selected assignments')}
                </button>
                <span className="text-sm text-gray-600">{t('{count} selected', { count: selectedKeys.size })}</span>
                <div className="basis-full border-t border-gray-100 pt-2 sm:basis-auto sm:border-t-0 sm:pt-0">
                  <span className="mr-2 text-xs font-semibold uppercase tracking-wide text-gray-500">{t('Batch training mode')}</span>
                  <span className="inline-flex max-w-full flex-wrap gap-2 align-middle">
                    {bulkCommands.map((command) => (
                      <button
                        key={command.label}
                        type="button"
                        onClick={() => applyBulkCommand(command.modes)}
                        disabled={!selectedKeys.size}
                        className="rounded border border-gray-300 bg-white px-2.5 py-1.5 text-sm hover:bg-gray-50 disabled:cursor-not-allowed disabled:opacity-50"
                        data-testid={`bulk-${command.label.replace(/\s+/g, '-').toLowerCase()}`}
                      >
                        {t(command.label)}
                      </button>
                    ))}
                  </span>
                </div>
              </div>

              <div className="max-h-[min(65vh,38rem)] space-y-5 overflow-y-auto p-4">
                <section aria-labelledby="pending-vocabulary-heading">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <h6 id="pending-vocabulary-heading" className="font-semibold text-gray-900">{t('Pending')}</h6>
                    <span className="text-xs text-gray-500">{t('{count} meanings', { count: pendingDraftGroups.length })}</span>
                  </div>
                  {pendingDraftGroups.length ? (
                    <div className="space-y-2">
                      {pendingDraftGroups.map((group) => (
                        <div
                          key={group.key}
                          className={`min-w-0 rounded border p-3 ${groupBorderClass(group.pendingModes)}`}
                          data-testid={`pending-row-${group.key}`}
                          data-sense-id={group.sense_id}
                        >
                          <div className="flex min-w-0 flex-wrap items-start gap-3">
                            <input
                              type="checkbox"
                              checked={selectedKeys.has(group.key)}
                              onChange={() => setSelectedKeys((current) => {
                                const next = new Set(current);
                                if (next.has(group.key)) next.delete(group.key);
                                else next.add(group.key);
                                return next;
                              })}
                              aria-label={senseActionLabel(group, t, 'Select {word}: {meaning}')}
                              className="mt-1 h-4 w-4 flex-none text-emerald-600"
                            />
                            <SenseSummary group={group} />
                            <ModeBadges modes={group.pendingModes} t={t} />
                          </div>
                          <div className="mt-3 flex min-w-0 flex-wrap gap-x-4 gap-y-2 border-t border-black/5 pt-2">
                            {STUDENT_TRAINING_MODES.map((mode) => {
                              const completed = (group.completedModes || []).includes(mode);
                              const checked = (group.pendingModes || []).includes(mode);
                              const onlyMode = checked && (group.pendingModes || []).length <= 1;
                              return (
                                <label key={mode} className="inline-flex min-w-0 items-center gap-2 text-sm text-gray-800">
                                  <input
                                    type="checkbox"
                                    checked={checked}
                                    disabled={completed || onlyMode}
                                    onChange={() => toggleMode(group, mode)}
                                    aria-label={t(modeLabelKeys[mode])}
                                    className="h-4 w-4 flex-none text-emerald-600"
                                  />
                                  <span className="break-words">{t(modeLabelKeys[mode])}</span>
                                  {completed && (
                                    <span className="text-xs text-gray-500">{t('Completed before')}</span>
                                  )}
                                </label>
                              );
                            })}
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">{t('No teacher-assigned meanings to learn')}</div>
                  )}
                </section>

                <section aria-labelledby="learned-vocabulary-heading">
                  <div className="mb-2 flex flex-wrap items-baseline justify-between gap-2">
                    <h6 id="learned-vocabulary-heading" className="font-semibold text-gray-900">{t('Learned')}</h6>
                    <span className="text-xs text-gray-500">{t('{count} meanings', { count: learnedGroups.length })}</span>
                  </div>
                  {learnedGroups.length ? (
                    <div className="space-y-2">
                      {learnedGroups.map((group) => (
                        <div
                          key={group.key}
                          className={`min-w-0 rounded border p-3 ${groupBorderClass(group.completedModes)}`}
                          data-testid={`learned-row-${group.key}`}
                          data-sense-id={group.sense_id}
                        >
                          <div className="flex min-w-0 flex-wrap items-start gap-3">
                            <SenseSummary group={group} />
                            <ModeBadges modes={group.completedModes} t={t} />
                          </div>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="rounded border border-gray-200 bg-gray-50 p-3 text-sm text-gray-600">{t('No history yet.')}</div>
                  )}
                </section>
              </div>

              <div className="sticky bottom-0 flex flex-wrap items-center gap-3 border-t border-gray-200 bg-white px-4 py-3">
                {confirmingRemoval && (
                  <div className="basis-full rounded border border-red-200 bg-red-50 p-3 text-sm text-red-900" role="alertdialog" aria-modal="true">
                    <p>{t('Remove {count} pending assignments? Completed learning records will stay unchanged.', { count: selectedKeys.size })}</p>
                    <div className="mt-3 flex flex-wrap gap-2">
                      <button type="button" onClick={() => setConfirmingRemoval(false)} disabled={saveState === 'saving'} className="rounded border border-gray-300 bg-white px-3 py-1.5 text-sm hover:bg-gray-50 disabled:opacity-50">
                        {t('Keep assignments')}
                      </button>
                      <button type="button" onClick={removeSelected} disabled={saveState === 'saving'} className="rounded bg-red-600 px-3 py-1.5 text-sm font-semibold text-white hover:bg-red-700 disabled:opacity-50">
                        {saveState === 'saving' ? t('Loading...') : t('Remove assignment')}
                      </button>
                    </div>
                  </div>
                )}
                {errorText && (
                  <div className="flex min-w-0 flex-1 flex-wrap items-center gap-2 text-sm text-red-700" role="alert">
                    <span className="break-words">{errorText}</span>
                    <button type="button" onClick={loadHistory} disabled={historyLoading || saveState === 'saving'} className="rounded border border-red-300 px-2 py-1 text-xs font-semibold hover:bg-red-50 disabled:opacity-50">
                      {t('Refresh latest progress')}
                    </button>
                  </div>
                )}
                {saveState === 'success' && <span className="text-sm font-semibold text-emerald-700">{t('Saved')}</span>}
                <div className="ml-auto flex flex-wrap gap-2">
                  <button type="button" onClick={closeManager} className="rounded border border-gray-300 bg-white px-3 py-2 text-sm text-gray-700 hover:bg-gray-50">
                    {t('Cancel')}
                  </button>
                  <button type="button" onClick={save} disabled={saveState === 'saving' || !dirtyKeys.size} className="rounded bg-emerald-600 px-3 py-2 text-sm font-semibold text-white hover:bg-emerald-700 disabled:cursor-not-allowed disabled:opacity-50">
                    {saveState === 'saving' ? t('Loading...') : t('Save')}
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </section>
  );
};

export default StudentVocabularyModeManager;
